import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useEngine, useLightsFrame} from './useEngine';
import {useAnalyses, useShow} from './useShow';
import AudioEditor from './AudioEditor';
import Timeline, {hasMedia, sceneStarts as sceneStartsOf, seekLanding} from './Timeline';
import {EffectShelf, useEffectCatalog} from './LightsLane';
import {C as THEME, panel as PANEL, heading as HEADING, btn as BTN} from './theme';
import {flatIndex, locate, sceneColor, sceneLabel, sectionOf} from './describe';
import {sceneStatus, StatusGlyph} from './sceneStatus';

// One page for the whole show, three tabs: Show (the room as it plays: main screen, operator and player phones,
// lights, Zed, timeline), Players (every stand-in player's phone) and Edit (blocks, clips and light cues).
// Previews are the real app running against the preview stack (tools/preview/start.sh): a separate
// event with stand-in players, driven by its own engine in manual mode. Each frame's *.localhost host
// decides who it is (the front door maps stage → control, maya → preview-maya).
const params = new URLSearchParams(window.location.search);
const PREVIEW_PORT = params.get('previewPort') || '4568';
const ENGINE_URL = params.get('engine') || 'ws://localhost:7301';
const ZED_URL = params.get('zed') || 'http://localhost:8642';
const PLAYERS = ['maya', 'dev', 'rosa', 'theo', 'priya', 'felix'];
const BOOT = Date.now();   // unique frame URLs per editor load, so frames can't reuse a stale cached page
const frameUrl = (who) => `http://${who}.localhost:${PREVIEW_PORT}/?f=${BOOT}`;

const C = {bg: '#0b0c0f', panel: '#15171c', line: '#262a33', text: '#e8eaed', dim: '#8a9099', accent: '#19f0a0'};
const panel = {background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', minHeight: 0};
const phoneHeading = {fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.dim, padding: '0 2px', height: 22, display: 'flex', alignItems: 'center', flex: 'none'};
const phoneScreen = {flex: 1, minHeight: 0, borderRadius: 12, overflow: 'hidden', border: `1px solid ${C.line}`};
const heading = {fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.dim, padding: '8px 12px'};

// fitHeight: the box takes the page's own shape at the height it's given (a phone panel exactly as wide as the phone).
const ScaledFrame = ({src, width, height, title, fitHeight}) => {
    const box = useRef(null);
    const [scale, setScale] = useState(0.3);
    useEffect(() => {
        const fit = () => box.current && setScale(fitHeight ? box.current.clientHeight / height
            : Math.min(box.current.clientWidth / width, box.current.clientHeight / height));
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(box.current);
        return () => ro.disconnect();
    }, [width, height, fitHeight]);
    return <div ref={box} style={{position: 'relative', width: fitHeight ? Math.round(width * scale) : '100%', height: '100%', overflow: 'hidden', background: '#000'}}>
        <iframe title={title} src={src} allow="autoplay; microphone" style={{
            width, height, border: 0, transform: `scale(${scale})`, transformOrigin: 'top left',
            position: 'absolute', left: (box.current ? (box.current.clientWidth - width * scale) / 2 : 0), top: 0,
        }}/>
    </div>;
};

// The top bar's middle: what's showing (folder › scene), and what moves the show on from here.
const SceneHeadline = ({doc, live, playback}) => {
    const [, tick] = useState(0);
    useEffect(() => { const t = setInterval(() => tick(n => n + 1), 250); return () => clearInterval(t); }, []);
    const block = live && doc.blocks[live.blockIndex], scene = block && block.scenes[live.sceneIndex];
    if (!scene) return <div/>;
    const section = sectionOf(doc, live.blockIndex);
    return <div style={{minWidth: 0, display: 'flex', alignItems: 'center', gap: 18}}>
        <span style={{fontSize: 24, fontWeight: 800, letterSpacing: -0.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
            {section && <span style={{color: section.color}}>{section.title}</span>}
            {section && <span style={{color: C.dim, fontWeight: 500, margin: '0 10px'}}>›</span>}
            <span style={{color: C.text}}>{sceneLabel(scene)}</span>
        </span>
        <StatusGlyph status={sceneStatus(doc, live, playback)}/>
    </div>;
};

// Show tab: the main screen's column is exactly as wide as the screen it can show (the phones, sized to the row's
// height, take what they need first), so whatever's left is split evenly around each phone.
const showMainWidth = ({w, h}) => {
    const row = h - 2 * 10 - 44 - 250 - 2 * 10;
    const phones = row * 390 / 844 + (row - 22) * 390 / 844;
    return Math.max(200, Math.floor(Math.min(w - 2 * 10 - 2 * 10 - phones, (row - 40) * 16 / 9)));
};

// The largest box of the given aspect that fits its space, centered: the main screen keeps its 16:9.
// `below` goes under the box (belowMin is kept free for it), the two spaced evenly down the column.
const AspectBox = ({ratio, children, style, below, belowMin = 0, top}) => {
    const outer = useRef(null);
    const [size, setSize] = useState(null);
    useEffect(() => {
        const fit = () => {
            const el = outer.current;
            if (!el) return;
            const w = el.clientWidth, h = el.clientHeight - belowMin;
            setSize(w / h > ratio ? {width: h * ratio, height: h} : {width: w, height: w / ratio});
        };
        fit();
        const ro = new ResizeObserver(fit);
        ro.observe(outer.current);
        return () => ro.disconnect();
    }, [ratio, belowMin]);
    return <div ref={outer} style={{width: '100%', height: '100%', display: 'flex', flexDirection: below ? 'column' : 'row', alignItems: top && !below ? 'flex-start' : 'center', justifyContent: below ? 'space-evenly' : 'center'}}>
        {size && <div style={{...style, ...size, position: 'relative', flex: 'none'}}>{children}</div>}
        {below && <div style={{flex: 'none', alignSelf: 'stretch', display: 'flex', justifyContent: 'center'}}>{below}</div>}
    </div>;
};

// The rig's six fixtures in a row. Live frames (r,g,b 0–255, dimmer 0–1) from the engine's lighting mixer;
// otherwise the color the selected scene asks for.
const LightsRow = ({fallbackColor, size = 20}) => {
    const frame = useLightsFrame();
    const fixtures = frame || Array.from({length: 6}, () => null);
    return <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: size * 1.1}}
        title={frame ? 'The rig, live' : 'The color this scene asks for'}>
        {fixtures.map((f, i) => {
            const color = f ? `rgb(${f.r},${f.g},${f.b})` : (fallbackColor || '#222');
            const dim = f ? f.dimmer : (fallbackColor ? 1 : 0.15);
            return <div key={i} style={{width: size, height: size, borderRadius: size / 2, background: color,
                opacity: 0.25 + 0.75 * dim, boxShadow: `0 0 ${3 + size * 0.4 * dim}px ${color}`}}/>;
        })}
    </div>;
};

// Zed's face (madam/face/server.py serves the playground), drawn from the show engine, which performs him: his voice,
// his simulation, his head over USB. The full playground (talk, lines, poses) is a click away.
const ZED_PAGE = `${ZED_URL}/mouth-playground.html?mirror=1&engine=${encodeURIComponent(ENGINE_URL)}`;
// The show's mix: one fader per bus (dB), with mute. The engine keeps it and every sound source follows it.
const MIX_BUSES = [['master', 'Master'], ['music', 'Music'], ['voice', 'Voice'], ['sfx', 'SFX']];
const MixPanel = ({mix, send}) => {
    const [open, setOpen] = useState(false);
    const [local, setLocal] = useState({});
    const last = useRef({});
    const level = (bus) => local[bus] ?? (mix && mix[bus] ? mix[bus].db : 0);
    const setDb = (bus, db) => {
        setLocal(l => ({...l, [bus]: db}));
        const now = Date.now();
        if (now - (last.current[bus] || 0) > 60) { last.current[bus] = now; send({cmd: 'mix', bus, db}); }
    };
    const commit = (bus) => { send({cmd: 'mix', bus, db: level(bus)}); setLocal(l => { const {[bus]: _, ...rest} = l; return rest; }); };
    return <div style={{position: 'relative'}}>
        <button onClick={() => setOpen(o => !o)} style={{...btn, background: open ? C.accent : btn.background, color: open ? C.bg : C.text}}>🎚 Mix</button>
        {open && <div style={{position: 'absolute', right: 0, top: 34, zIndex: 100, width: 330, padding: '10px 14px', borderRadius: 10,
            background: C.panel, border: `1px solid ${C.line}`, boxShadow: '0 12px 40px rgba(0,0,0,.6)'}}>
            {MIX_BUSES.map(([bus, label]) => {
                const muted = !!(mix && mix[bus] && mix[bus].muted), db = level(bus);
                return <div key={bus} style={{display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', opacity: muted ? 0.5 : 1}}>
                    <span style={{width: 52, fontSize: 12, fontWeight: bus === 'master' ? 700 : 400}}>{label}</span>
                    <input type="range" min={-40} max={6} step={0.5} value={db} style={{flex: 1, accentColor: C.accent}}
                        onChange={e => setDb(bus, +e.target.value)} onMouseUp={() => commit(bus)} onTouchEnd={() => commit(bus)}
                        onDoubleClick={() => { setLocal(l => ({...l, [bus]: 0})); send({cmd: 'mix', bus, db: 0}); }}/>
                    <span style={{width: 50, textAlign: 'right', fontSize: 11, fontVariantNumeric: 'tabular-nums', color: C.dim}}>{db > 0 ? '+' : ''}{db.toFixed(1)} dB</span>
                    <button onClick={() => send({cmd: 'mix', bus, muted: !muted})} title={muted ? 'Unmute' : 'Mute'}
                        style={{...btn, padding: '2px 7px', fontSize: 11, background: muted ? '#ff5d5d' : btn.background, color: muted ? '#000' : C.text}}>M</button>
                </div>;
            })}
            <div style={{fontSize: 10, color: C.dim, marginTop: 4}}>Double-click a fader to reset it to 0 dB. Music = block music + title videos · Voice = Zed · SFX = sound cues.</div>
        </div>}
    </div>;
};

// Zed's face lives once, at the editor's root, for as long as the editor is open, so switching tabs never reloads
// it. The Show tab's Zed panel is a slot it fills; on other tabs it shrinks to a corner thumbnail.
let zedSlotEl = null;
const zedSlotListeners = new Set();
const setZedSlot = (el) => { zedSlotEl = el; zedSlotListeners.forEach(fn => fn(el)); };

// hidden: out of sight but still running (an iframe Chrome thinks is invisible gets throttled, so it's transparent instead).
const ZedHost = ({performing, hidden}) => {
    const [slot, setSlot] = useState(zedSlotEl);
    const [rect, setRect] = useState(null);
    const [reachable, setReachable] = useState(null);
    useEffect(() => { zedSlotListeners.add(setSlot); return () => zedSlotListeners.delete(setSlot); }, []);
    useEffect(() => {
        if (!slot) { setRect(null); return; }
        const measure = () => { const r = slot.getBoundingClientRect(); setRect({left: r.left, top: r.top, width: r.width, height: r.height}); };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(slot);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [slot]);
    // Only decides whether to show "offline" help; once mounted, the face is never torn down for a missed check.
    useEffect(() => {
        let misses = 0;
        const check = () => fetch(ZED_PAGE, {mode: 'no-cors'}).then(() => { misses = 0; setReachable(true); })
            .catch(() => { if (++misses >= 2) setReachable(r => r === null ? false : r === true ? 'lost' : r); });
        check();
        const t = setInterval(check, 5000);
        return () => clearInterval(t);
    }, []);
    const box = rect || {left: window.innerWidth - 196, top: window.innerHeight - 196, width: 180, height: 180};
    const scale = Math.min(box.width / 860, box.height / 790);
    return <div className="zed-host" style={{position: 'fixed', ...box, zIndex: rect ? 5 : 50, overflow: 'hidden', ...(hidden ? {opacity: 0, pointerEvents: 'none'} : {}), background: rect ? 'transparent' : '#0a0806',
        borderRadius: rect ? 0 : 12, boxShadow: rect ? 'none' : '0 8px 30px rgba(0,0,0,.7)', border: rect ? 'none' : `1px solid ${C.line}`}}>
        {reachable !== false && <div style={{position: 'absolute', top: 0, left: '50%', width: 860 * scale, height: 790 * scale, marginLeft: -430 * scale, overflow: 'hidden'}}>
            <iframe title="zed" src={ZED_PAGE} allow="microphone; camera; autoplay; serial" style={{width: 1400, height: 900, border: 0, transform: `scale(${scale})`,
                transformOrigin: 'top left', position: 'absolute', top: 0, left: 0, pointerEvents: rect ? 'auto' : 'none'}}/>
        </div>}
        {reachable === false && <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.dim, fontSize: 12, padding: 12, textAlign: 'center'}}>
            <span>Zed offline<br/><code style={{fontSize: 10, wordBreak: 'break-all'}}>cd ~/workspace/madam/face && python3 server.py</code></span></div>}
        {!rect && <div style={{position: 'absolute', left: 6, bottom: 4, fontSize: 9, color: performing ? '#ff5a3c' : C.dim, textShadow: '0 1px 2px #000'}}>
            {performing ? '● Zed live' : '○ Zed not performing'}</div>}
        {rect && <>
            <style>{'.zed-host .zed-open { opacity: 0; transition: opacity 120ms } .zed-host:hover .zed-open { opacity: 1 }'}</style>
            <a className="zed-open" href={ZED_URL + '/mouth-playground.html'} target="zed" style={{position: 'absolute', right: 6, top: 6, ...btn, textDecoration: 'none', background: '#1f232bdd'}}>Open playground ↗</a>
            {reachable !== false && !performing && <div title="The show engine isn't performing him" style={{position: 'absolute', left: 0, right: 0, bottom: 4, textAlign: 'center', fontSize: 11, color: '#ff5d5d', textShadow: '0 1px 2px #000'}}>Zed not performing</div>}
        </>}
    </div>;
};

const ZedPanel = () => {
    const slot = useRef(null);
    useEffect(() => { setZedSlot(slot.current); return () => setZedSlot(null); }, []);
    return <div ref={slot} style={{width: '100%', aspectRatio: '860 / 790'}}/>;
};

// Every stand-in player's phone at once, plus the main screen — like /tile, driven by the engine.
const usePreviewPlayers = () => {
    const [players, setPlayers] = useState(PLAYERS.map(p => ({id: p, name: p})));
    useEffect(() => {
        fetch('/users/').then(r => r.json()).then(users => {
            const preview = users.filter(u => u.token.startsWith('preview-'))
                .map(u => ({id: u.token.slice('preview-'.length), name: u.name})).reverse();
            if (preview.length) setPlayers(preview);
        }).catch(() => {});
    }, []);
    return players;
};

// The editor's tabs.
const VIEWS = [['show', 'Show'], ['players', 'Players'], ['edit', 'Edit']];
const RENAMED = {room: 'show', tiles: 'players', timeline: 'edit'};      // what a tab saved before the rename is now
const initialView = (params) => {
    let v = params.get('view');
    try { v = v || localStorage.getItem('editor.view'); } catch {}
    v = RENAMED[v] || v;
    return VIEWS.some(([id]) => id === v) ? v : 'show';
};

// The most columns that still fit every phone at phone shape (the /tile page's rule): each as large as the space allows.
const TILE_LABEL = 20, TILE_GAP = 12;
const tileLayout = (n, W, H) => {
    let best = {cols: 1, w: 0};
    for (let cols = 1; cols <= n; cols++) {
        const rows = Math.ceil(n / cols);
        const w = Math.min((W - (cols - 1) * TILE_GAP) / cols, ((H - (rows - 1) * TILE_GAP) / rows - TILE_LABEL) * 390 / 844);
        if (w > best.w) best = {cols, w};
    }
    return {cols: best.cols, w: Math.floor(best.w), h: Math.floor(best.w * 844 / 390)};
};

const Players = () => {
    const players = usePreviewPlayers();
    const box = useRef(null);
    const [size, setSize] = useState(null);
    useEffect(() => {
        const el = box.current, ro = new ResizeObserver(() => setSize({w: el.clientWidth, h: el.clientHeight}));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const L = size && tileLayout(players.length, size.w, size.h);
    return <div ref={box} style={{height: '100%', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
        {L && <div style={{display: 'grid', gap: TILE_GAP, gridTemplateColumns: `repeat(${L.cols}, ${L.w}px)`}}>
            {players.map(p => <div key={p.id} style={{display: 'flex', flexDirection: 'column'}}>
                <div style={{...phoneScreen, flex: 'none', width: L.w, height: L.h}}><ScaledFrame title={p.id} src={frameUrl(p.id)} width={390} height={844}/></div>
                <div style={{height: TILE_LABEL, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: C.dim}}>{p.name}</div>
            </div>)}
        </div>}
    </div>;
};

// The whole show left to right: one thumbnail per scene (rendered by tools/preview/thumbs.cjs),
// grouped by block, playhead on the live scene. Click to jump.
// Blinks on every beat of the engine's beat clock (bigger on the downbeat) — the same clock the lights flash to.
const BeatDot = ({beat}) => {
    const dot = useRef(null), label = useRef(null);
    useEffect(() => {
        let raf;
        const tick = () => {
            if (beat && dot.current) {
                const beats = (Date.now() - beat.phaseOrigin) * beat.bpm / 60000;
                const frac = beats - Math.floor(beats), k = Math.floor(beats);
                const down = ((k % beat.beatsPerBar) + beat.beatsPerBar) % beat.beatsPerBar === 0;
                const flash = Math.max(0, 1 - frac / 0.35);
                dot.current.style.opacity = 0.15 + 0.85 * flash;
                dot.current.style.transform = `scale(${1 + (down ? 0.5 : 0.2) * flash})`;
                dot.current.style.background = down ? '#fff' : THEME.accent;
                if (label.current) label.current.textContent = `${Math.floor(k / beat.beatsPerBar) + 1}.${((k % beat.beatsPerBar) + beat.beatsPerBar) % beat.beatsPerBar + 1}`;
            } else if (dot.current) { dot.current.style.opacity = 0.15; dot.current.style.transform = 'scale(1)'; if (label.current) label.current.textContent = '–'; }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [beat]);
    return <div style={{display: 'flex', alignItems: 'center', gap: 8}} title="Beat clock">
        <div ref={dot} style={{width: 16, height: 16, borderRadius: 8, background: THEME.accent, opacity: 0.15}}/>
        <span ref={label} style={{fontSize: 12, fontVariantNumeric: 'tabular-nums', color: THEME.dim, minWidth: 34}}>–</span>
        {beat && <span style={{fontSize: 11, color: THEME.dim}}>{beat.bpm.toFixed(1)} BPM</span>}
    </div>;
};

// The six fixtures in a row, live from the lighting mixer.
const MiniRig = () => <MiniRigView frame={useLightsFrame()}/>;
const MiniRigView = ({frame}) => <div style={{display: 'flex', gap: 7, alignItems: 'center'}} title="Lights (live)">
    {(frame || Array.from({length: 6}, () => null)).map((f, i) => {
        const color = f ? `rgb(${f.r},${f.g},${f.b})` : '#222', dim = f ? f.dimmer : 0.1;
        return <div key={i} style={{width: 14, height: 14, borderRadius: 7, background: color, opacity: 0.2 + 0.8 * dim,
            boxShadow: dim > 0.3 ? `0 0 ${6 + 14 * dim}px ${color}` : 'none'}}/>;
    })}
</div>;

// Songs in the media library: drag one onto a block's music lane, then trim it there. Drop audio files
// from Finder here to import them.
const useSongs = () => {
    const [songs, setSongs] = useState([]);
    const refetch = () => fetch('/engine/media').then(r => r.json()).then(l => Array.isArray(l) && setSongs(l)).catch(() => {});
    useEffect(() => { refetch(); const t = setInterval(refetch, 5000); return () => clearInterval(t); }, []);
    return {songs, refetch};
};

const ClipShelf = ({doc, onEditClips}) => {
    const clips = Object.values(doc.clips || {});
    const {songs, refetch} = useSongs();
    const [over, setOver] = useState(false);
    const importFiles = (files) => Promise.all([...files].filter(f => /^audio\//.test(f.type) || /\.(mp3|wav|aiff?|m4a|flac)$/i.test(f.name))
        .map(f => fetch(`/engine/media/upload?name=${encodeURIComponent(f.name)}`, {method: 'POST', body: f}))).then(refetch);
    return <div style={{...PANEL, display: 'flex', flexDirection: 'column', flex: 1}}>
        <div style={{...HEADING, display: 'flex', alignItems: 'center'}}>
            <span style={{flex: 1}}>Clips</span>
            <button onClick={onEditClips} style={{...BTN, padding: '2px 9px', fontSize: 11}}>✂ clip editor</button>
        </div>
        <div onDragOver={e => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setOver(true); } }} onDragLeave={() => setOver(false)}
            onDrop={e => { if (!e.dataTransfer.files.length) return; e.preventDefault(); setOver(false); importFiles(e.dataTransfer.files); }}
            style={{overflowY: 'auto', flex: 1, padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: 6, outline: over ? `2px dashed ${THEME.accent}` : 'none', outlineOffset: -4}}>
            <div style={{fontSize: 10, color: THEME.dim, letterSpacing: 0.8, textTransform: 'uppercase', padding: '2px 2px 0'}}>Songs · drag onto a block, trim it there</div>
            {songs.length === 0 && <div style={{color: THEME.dim, fontSize: 12, padding: 6}}>Drop audio files here to import them.</div>}
            {songs.map(m => <div key={m.id} draggable={m.status === 'ready'} onDragStart={e => { e.dataTransfer.setData('application/x-song', JSON.stringify({id: m.id, name: m.name})); e.dataTransfer.effectAllowed = 'copy'; }}
                title={m.status === 'ready' ? 'Drag onto a block\'s music lane' : 'Analyzing beats…'} style={{padding: '6px 10px', borderRadius: 8, background: '#1a1d24', border: `1px solid ${THEME.line}`,
                cursor: m.status === 'ready' ? 'grab' : 'progress', opacity: m.status === 'ready' ? 1 : 0.5, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                ♫ {m.name}{m.status !== 'ready' ? ' · analyzing…' : ''}</div>)}
            <div style={{fontSize: 10, color: THEME.dim, letterSpacing: 0.8, textTransform: 'uppercase', padding: '8px 2px 0'}}>Saved clips</div>
            {clips.length === 0 && <div style={{color: THEME.dim, fontSize: 12, padding: 6}}>No clips yet. Open the clip editor, pick part of a song, save it — then drag it onto a block's music lane.</div>}
            {clips.map(c => <div key={c.id} draggable onDragStart={e => { e.dataTransfer.setData('application/x-clip', c.id); e.dataTransfer.effectAllowed = 'copy'; }}
                title="Drag onto a block's music lane" style={{padding: '7px 10px', borderRadius: 8, background: '#1f232b', border: `1px solid ${THEME.line}`, cursor: 'grab'}}>
                <div style={{fontSize: 13}}>♪ {c.name}</div>
                <div style={{fontSize: 11, color: THEME.dim}}>{(c.end - c.start).toFixed(1)}s{c.grid ? ` · ${c.grid.bpm.toFixed(1)} BPM` : ''}</div>
            </div>)}
        </div>
    </div>;
};


export const Editor = () => {
    const engine = useEngine(ENGINE_URL);
    const show = useShow(engine.lastEvent);
    const effectCatalog = useEffectCatalog();
    const doc = show.doc;
    const analyses = useAnalyses([...Object.values(doc.clips || {}).map(c => c.media),
        ...doc.blocks.flatMap(b => (b.segments || []).filter(g => g.kind === 'video').map(g => `video:${g.video}`))]);
    const [thumbsStamp, setThumbsStamp] = useState(null);
    useEffect(() => { fetch('/thumbs/index.json').then(r => r.json()).then(i => setThumbsStamp(i.renderedAt)).catch(() => {}); }, []);
    const [clipTool, setClipTool] = useState(false);
    const [selected, setSelected] = useState({blockIndex: 0, sceneIndex: 0});
    const [cursor, setCursor] = useState(null);         // the playhead when parked: {block: index, t: seconds}
    const [player, setPlayer] = useState(PLAYERS[0]);
    const players = usePreviewPlayers();
    const [view, setView] = useState(() => initialView(params));
    const [win, setWin] = useState({w: window.innerWidth, h: window.innerHeight});
    useEffect(() => { const on = () => setWin({w: window.innerWidth, h: window.innerHeight}); window.addEventListener('resize', on); return () => window.removeEventListener('resize', on); }, []);
    const switchView = (v) => { setView(v); try { localStorage.setItem('editor.view', v); } catch {} };
    const live = useMemo(() => (engine.state && engine.state.sceneIndex != null) ? locate(doc, engine.state.sceneIndex) : null, [engine.state, doc]);

    useEffect(() => { document.title = 'Show Editor'; document.body.style.background = C.bg; }, []);
    useEffect(() => { if (live) setSelected(live); }, [live]);

    const select = (b, s) => {
        setSelected({blockIndex: b, sceneIndex: s});
        engine.send({cmd: 'goto', target: flatIndex(doc, b, s)});
    };
    const scene = doc.blocks[selected.blockIndex].scenes[selected.sceneIndex];

    // Playhead: a click inside the live scene seeks to that moment; a click on any other scene goes to its start.
    const seek = (b, t) => {
        const block = doc.blocks[b], land = seekLanding(doc, live, b, t);
        if (!land) return;
        const {s, isLive, timed: timedHere} = land;
        t = land.t;
        setCursor({block: b, t});
        if (isLive && !(engine.playback && engine.playback.block === block.id)) return;    // never re-send the live scene: that restarts it
        // Entering a block plays it (the show follows its music), so play from the click rather than letting the
        // jump start it from the scene's top. Inside a stopped block the click just parks the playhead.
        if (hasMedia(doc, block) && timedHere && (engine.playback || !live || live.blockIndex !== b)) {
            setSelected({blockIndex: b, sceneIndex: s}); engine.send({cmd: 'play-block', block: block.id, from: t});
        } else select(b, s);
    };
    const playSelected = () => {
        const block = doc.blocks[selected.blockIndex];
        if (!hasMedia(doc, block)) return;
        engine.send({cmd: 'play-block', block: block.id, from: cursor && cursor.block === selected.blockIndex ? cursor.t : 0});
    };
    const togglePlay = useRef();
    // One pause for the whole show (the operator's too): music, Zed and the games hold, and resume carries on.
    // The playhead parks where the music was.
    const showPaused = !!(engine.state && engine.state.paused);
    togglePlay.current = () => {
        if (showPaused) return engine.send({cmd: 'resume-show'});
        const pb = engine.playback;
        if (!pb) return playSelected();
        const b = doc.blocks.findIndex(x => x.id === pb.block), t = Math.max(0, (Date.now() - pb.startedAt) / 1000);
        engine.send({cmd: 'pause-show'});
        if (b < 0) return;
        const starts = sceneStartsOf(doc, doc.blocks[b]);
        setCursor({block: b, t});
        setSelected({blockIndex: b, sceneIndex: starts.reduce((cur, st, i) => st <= t + 1e-6 ? i : cur, 0)});
    };
    useEffect(() => {
        const onKey = (e) => {
            if (e.code !== 'Space' || (e.target.closest && e.target.closest('input, textarea, select'))) return;
            e.preventDefault(); togglePlay.current();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    return <div style={{
        position: 'fixed', inset: 0, background: C.bg, color: C.text, fontFamily: 'Inter, system-ui, sans-serif',
        display: 'grid', gap: 10, padding: 10, boxSizing: 'border-box',
        gridTemplateColumns: view === 'show' ? `1fr ${showMainWidth(win)}px 1fr` : '1fr 300px',
        gridTemplateRows: view === 'edit' ? '44px 1fr 0px 0px' : view === 'show' ? '44px 1fr 250px' : '44px 1fr',
        gridTemplateAreas: view === 'edit' ? `"bar bar" "edit edit" "edit edit" "edit edit"`
            : view === 'players' ? `"bar bar" "players players"`
            : `"bar bar bar" "phone main operator" "timeline timeline timeline"`,
    }}>
        <ZedHost performing={!!(engine.state && engine.state.zedPerformer)} hidden={view === 'players'}/>
        <div style={{gridArea: 'bar', display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 16, padding: '0 6px'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: 16, minWidth: 0}}>
            <div style={{display: 'flex', border: `1px solid ${C.line}`, borderRadius: 6, overflow: 'hidden'}}>
                {VIEWS.map(([v, label]) => <button key={v} onClick={() => switchView(v)} style={{
                    ...btn, border: 0, borderRadius: 0,
                    background: view === v ? C.accent : '#1f232b', color: view === v ? C.bg : C.text,
                }}>{label}</button>)}
            </div>
            {!engine.connected && <span style={{fontSize: 12, color: '#ff5d5d'}}>● preview engine offline — run tools/preview/start.sh</span>}

            </div>
            <SceneHeadline doc={doc} live={live} playback={engine.playback}/>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16}}>
            <button onClick={() => engine.send({cmd: 'test-beep'})} title="Sound check: four seconds of beeps through the room, the lights flashing with them. The show doesn't move."
                style={BTN}>🔊 Test</button>
            <MixPanel mix={engine.state && engine.state.mix} send={engine.send}/>
            </div>
        </div>
        {view === 'edit' ? <div style={{gridArea: 'edit', minHeight: 0, display: 'grid', gridTemplateColumns: '260px 1fr', gap: 10}}>
            <div style={{display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0}}>
                <div style={{flex: 1, minHeight: 0, display: 'flex'}}><ClipShelf doc={doc} onEditClips={() => setClipTool(t => !t)}/></div>
                <div style={{...PANEL, flexShrink: 0}}>
                    <div style={HEADING}>Light effects</div>
                    <div style={{padding: '0 8px 8px'}}><EffectShelf catalog={effectCatalog}/></div>
                </div>
            </div>
            <div style={{...PANEL, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '10px 0 0'}}>
                {!clipTool && <div style={{display: 'flex', alignItems: 'center', gap: 16, padding: '0 14px 10px', borderBottom: `1px solid ${THEME.line}`, marginBottom: 10}}>
                    {showPaused
                        ? <button onClick={() => engine.send({cmd: 'resume-show'})} style={{...BTN, background: '#ffb347', color: THEME.bg, minWidth: 140}}>▶ Resume</button>
                        : engine.playback
                        ? <button onClick={() => engine.send({cmd: 'pause-show'})} style={{...BTN, background: THEME.accent, color: THEME.bg, minWidth: 140}}>❚❚ Pause</button>
                        : <button onClick={playSelected}
                            disabled={!hasMedia(doc, doc.blocks[selected.blockIndex])}
                            title={hasMedia(doc, doc.blocks[selected.blockIndex]) ? '' : 'This block has no music yet'}
                            style={{...BTN, minWidth: 140, opacity: hasMedia(doc, doc.blocks[selected.blockIndex]) ? 1 : 0.4}}>▶ Play “{doc.blocks[selected.blockIndex].label}”{cursor && cursor.block === selected.blockIndex && cursor.t > 0 ? ` from ${cursor.t.toFixed(1)}s` : ''}</button>}
                    <span style={{fontSize: 11, color: THEME.dim}}>space = play/pause · click the timeline to move the playhead</span>
                    <BeatDot beat={engine.beat}/>
                    <span style={{width: 1, height: 20, background: THEME.line}}/>
                    <MiniRig/>
                    <span style={{flex: 1}}/>
                    {engine.state && !engine.state.audio && <span style={{fontSize: 11, color: '#ffb347'}}>engine audio is off — start the preview with audio to hear playback</span>}
                </div>}
                {clipTool ? <div style={{flex: 1, minHeight: 0, padding: '0 10px 10px', display: 'flex', flexDirection: 'column'}}>
                    <div style={{display: 'flex', marginBottom: 8}}><span style={{flex: 1}}/><button onClick={() => { setClipTool(false); show.refetch(); }} style={BTN}>← back to timeline</button></div>
                    <div style={{flex: 1, minHeight: 0}}><AudioEditor/></div>
                </div> : <Timeline mode="edit" doc={doc} analyses={analyses} selected={selected} live={live}
                    edits={show} thumbsStamp={thumbsStamp} scaleKey="tl.edit.pps" playback={engine.playback}
                    onAudition={(cue) => engine.send({cmd: 'lights-effect', cue})} cursor={cursor} onSeek={seek}/>}
            </div>
        </div>
            : view === 'players' ? <div style={{gridArea: 'players', minHeight: 0}}><Players/></div> : <>
        {/* Show: the operator's console and a player's phone either side of the main screen, always both. */}
        <div style={{gridArea: 'operator', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <div style={phoneScreen}><ScaledFrame title="operator" src={frameUrl('operator')} width={390} height={844} fitHeight/></div>
        </div>
        <div style={{gridArea: 'main', minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column'}}>
            {/* the grid gap under this column belongs to the lights' space too, so they center between the screen and the timeline */}
            <div style={{flex: 1, minHeight: 0, marginBottom: -10}}><AspectBox ratio={16 / 9} style={panel} belowMin={50}
                below={<LightsRow fallbackColor={sceneColor(scene)}/>}>
                <ScaledFrame title="stage" src={frameUrl('stage')} width={1920} height={1080}/>
            </AspectBox></div>
        </div>
        <div style={{gridArea: 'phone', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
            <div style={phoneScreen}><ScaledFrame key={player} title="player" src={frameUrl(player)} width={390} height={844} fitHeight/></div>
            <div style={{...phoneHeading, justifyContent: 'center'}} title="Whose phone this is">
                <select value={player} onChange={e => setPlayer(e.target.value)} style={{background: 'transparent', color: C.dim, border: 0, fontSize: 11, padding: 0, cursor: 'pointer'}}>
                    {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
            </div>
        </div>
        </>}
        {view === 'show' && <div style={{gridArea: 'timeline', minHeight: 0, minWidth: 0, display: 'flex', gap: 10}}>
            <div style={{flex: 1, minWidth: 0, minHeight: 0, paddingTop: 6}}>
                <Timeline mode="compact" doc={doc} analyses={analyses} selected={selected} live={live} thumbsStamp={thumbsStamp} playback={engine.playback} cursor={cursor} onSeek={seek}/>
            </div>
            {view === 'show' && <div style={{width: 272, flex: 'none', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <ZedPanel/>
            </div>}
        </div>}
    </div>;
};

const btn = {background: '#1f232b', color: '#e8eaed', border: '1px solid #262a33', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12};

export default Editor;
